import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosHttp from "services/axiosHttp";

const initialState = {
	assignments: [],
	users: [],
	userGroups: [],
	isLoading: false,
	isSaving: false,
	isLoadingUsers: false,
	isLoadingUserGroups: false,
	error: null,
};

// Fetch LMS users with filters
export const fetchLmsUsers = createAsyncThunk("courseAssignment/fetchLmsUsers", async ({ page, limit, filters }, { rejectWithValue }) => {
	try {
		const hasEmployee = filters?.hasEmployee ?? true;
		const queryParams = new URLSearchParams({
			page: page ?? 1,
			limit: limit ?? 10000,
			hasEmployee: hasEmployee.toString(),
			search: filters?.search ?? "",
			departmentId: hasEmployee && filters?.departmentId ? filters.departmentId.toString() : "",
			valueChainId: hasEmployee && filters?.valueChainId ? filters.valueChainId.toString() : "",
			companyId: hasEmployee && filters?.companyId ? filters.companyId.toString() : "",
			userGroupId: !hasEmployee && filters?.userGroupId ? filters.userGroupId.toString() : "",
		});

		const response = await axiosHttp.get(`lms/users?${queryParams}`);
		return response.data;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

// Fetch user groups for external users
export const fetchUserGroups = createAsyncThunk("courseAssignment/fetchUserGroups", async (_, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.get("lms/users/user-groups");
		return response.data;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

// Fetch assignments for a specific course
export const fetchCourseAssignments = createAsyncThunk("courseAssignment/fetchCourseAssignments", async (courseId, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.get(`lms/courses/${courseId}/assignments`);

		return response.data;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

// Assign employees to a course (Replace Strategy)
export const assignEmployeesToCourse = createAsyncThunk(
	"courseAssignment/assignEmployeesToCourse",
	async ({ courseId, employeeUuids }, { rejectWithValue }) => {
		try {
			const response = await axiosHttp.post(`lms/courses/${courseId}/assign`, {
				employee_ids: employeeUuids,
			});
			return response.data;
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message);
		}
	},
);

// Optional: Unassign specific employees
export const unassignEmployeesFromCourse = createAsyncThunk(
	"courseAssignment/unassignEmployeesFromCourse",
	async ({ courseId, employeeUuids }, { rejectWithValue }) => {
		try {
			const response = await axiosHttp.delete(`lms/courses/${courseId}/assignments`, {
				data: { employee_ids: employeeUuids },
			});
			return response.data;
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message);
		}
	},
);

const courseAssignmentSlice = createSlice({
	name: "courseAssignment",
	initialState,
	reducers: {
		clearAssignments: (state) => {
			state.assignments = [];
			state.error = null;
		},
		clearError: (state) => {
			state.error = null;
		},
		clearUsers: (state) => {
			state.users = [];
		},
	},
	extraReducers: (builder) => {
		builder
			// Fetch LMS Users
			.addCase(fetchLmsUsers.pending, (state) => {
				state.isLoadingUsers = true;
				state.error = null;
			})
			.addCase(fetchLmsUsers.fulfilled, (state, action) => {
				state.isLoadingUsers = false;
				state.users = action.payload.data || [];
			})
			.addCase(fetchLmsUsers.rejected, (state, action) => {
				state.isLoadingUsers = false;
				state.error = action.payload;
				state.users = [];
			})

			// Fetch User Groups
			.addCase(fetchUserGroups.pending, (state) => {
				state.isLoadingUserGroups = true;
				state.error = null;
			})
			.addCase(fetchUserGroups.fulfilled, (state, action) => {
				state.isLoadingUserGroups = false;
				state.userGroups = action.payload.data || [];
			})
			.addCase(fetchUserGroups.rejected, (state, action) => {
				state.isLoadingUserGroups = false;
				state.error = action.payload;
				state.userGroups = [];
			})

			// Fetch Course Assignments
			.addCase(fetchCourseAssignments.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchCourseAssignments.fulfilled, (state, action) => {
				state.isLoading = false;
				state.assignments = action.payload.data?.assigned_employees || [];
			})
			.addCase(fetchCourseAssignments.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
				state.assignments = [];
			})

			// Assign Employees to Course
			.addCase(assignEmployeesToCourse.pending, (state) => {
				state.isSaving = true;
				state.error = null;
			})
			.addCase(assignEmployeesToCourse.fulfilled, (state, action) => {
				state.isSaving = false;
			})
			.addCase(assignEmployeesToCourse.rejected, (state, action) => {
				state.isSaving = false;
				state.error = action.payload;
			})

			// Unassign Employees
			.addCase(unassignEmployeesFromCourse.pending, (state) => {
				state.isSaving = true;
				state.error = null;
			})
			.addCase(unassignEmployeesFromCourse.fulfilled, (state, action) => {
				state.isSaving = false;
			})
			.addCase(unassignEmployeesFromCourse.rejected, (state, action) => {
				state.isSaving = false;
				state.error = action.payload;
			});
	},
});

export const { clearAssignments, clearError, clearUsers } = courseAssignmentSlice.actions;
export default courseAssignmentSlice.reducer;
